'use client';
import React, { useState, useMemo } from 'react';
import Icon from '@/components/ui/AppIcon';
import KnowledgeArticleDetail from './KnowledgeArticleDetail';
import AIAskPanel from './AIAskPanel';


type Article = {
  id: string;
  title: string;
  agency: string;
  category: string;
  updated: string;
  views: number;
  verified: boolean;
  tags: string[];
  status: 'Published' | 'Under Review' | 'Archived';
  summary: string;
};

// Backend integration point: replace with /api/knowledge-base/articles
const articles: Article[] = [
  { id: 'kb-001', title: 'How to Update MySejahtera Mobile Number', agency: 'Kementerian Kesihatan Malaysia', category: 'MySejahtera', updated: '04 Aug 2026', views: 4821, verified: true, tags: ['MySejahtera', 'Mobile', 'Account'], status: 'Published', summary: 'Step-by-step guide for citizens to update their registered mobile number in the MySejahtera application, including identity verification requirements.' },
  { id: 'kb-002', title: 'MySejahtera Account Recovery Procedure', agency: 'Kementerian Kesihatan Malaysia', category: 'MySejahtera', updated: '01 Aug 2026', views: 3214, verified: true, tags: ['MySejahtera', 'Recovery', 'Account'], status: 'Published', summary: 'Complete procedure for recovering access to a MySejahtera account when the citizen has lost access to their registered mobile number or email.' },
  { id: 'kb-003', title: 'Malaysian Passport Renewal — Standard Procedure', agency: 'Jabatan Imigresen Malaysia', category: 'Passport', updated: '10 Aug 2026', views: 8942, verified: true, tags: ['Passport', 'Renewal', 'Immigration', 'Documents'], status: 'Published', summary: 'Complete guide covering required documents, fees, processing times and appointment booking for Malaysian passport renewal at immigration offices nationwide.' },
  { id: 'kb-004', title: 'Passport Renewal for Overseas Citizens', agency: 'Jabatan Imigresen Malaysia', category: 'Passport', updated: '08 Aug 2026', views: 2841, verified: true, tags: ['Passport', 'Overseas', 'Embassy'], status: 'Published', summary: 'Procedure for Malaysian citizens residing overseas to renew their passport through Malaysian diplomatic missions and consulates.' },
  { id: 'kb-005', title: 'STR Application — Eligibility and Requirements', agency: 'Kementerian Pembangunan Luar Bandar', category: 'Government Assistance', updated: '12 Aug 2026', views: 6120, verified: true, tags: ['STR', 'Assistance', 'Eligibility', 'Application'], status: 'Published', summary: 'Eligibility criteria, income thresholds, required documentation and application process for the Sumbangan Tunai Rahmah programme.' },
  { id: 'kb-006', title: 'Road Tax Renewal — JPJ Online Service', agency: 'Jabatan Pengangkutan Jalan', category: 'Road Tax', updated: '05 Aug 2026', views: 5201, verified: true, tags: ['Road Tax', 'JPJ', 'Vehicle', 'Online'], status: 'Published', summary: 'Guide for renewing vehicle road tax through JPJ online portal, including insurance requirements and payment methods.' },
  { id: 'kb-007', title: 'MyDigital ID Registration and Verification', agency: 'Jabatan Pendaftaran Negara', category: 'Digital Identity', updated: '14 Aug 2026', views: 3892, verified: true, tags: ['MyDigital ID', 'JPN', 'Identity', 'Verification'], status: 'Published', summary: 'Complete registration process for MyDigital ID, the national digital identity platform for Malaysian citizens accessing Government digital services.' },
  { id: 'kb-008', title: 'KWSP Pengeluaran Akaun 2 — Eligibility', agency: 'KWSP', category: 'KWSP', updated: '03 Aug 2026', views: 4102, verified: true, tags: ['KWSP', 'Withdrawal', 'EPF', 'Account 2'], status: 'Published', summary: 'Eligibility requirements and application procedure for KWSP Account 2 partial withdrawal for housing, education and medical purposes.' },
  { id: 'kb-009', title: 'Income Tax e-Filing Guide — LHDN', agency: 'LHDN', category: 'Income Tax', updated: '15 Jul 2026', views: 7841, verified: true, tags: ['LHDN', 'Tax', 'e-Filing', 'Income Tax'], status: 'Published', summary: 'Step-by-step guide for submitting annual income tax returns through the MyTax portal, including login, form selection and submission.' },
  { id: 'kb-010', title: 'PERKESO EIS Claim Submission Procedure', agency: 'PERKESO', category: 'Employment Insurance', updated: '20 Jul 2026', views: 1820, verified: true, tags: ['PERKESO', 'EIS', 'Claim', 'Employment'], status: 'Published', summary: 'Procedure for submitting Employment Insurance System claims through the PERKESO online portal following retrenchment or voluntary separation.' },
];

const categories = [
  { key: 'all', label: 'All Articles', icon: 'BookOpenIcon', count: articles.length },
  { key: 'MySejahtera', label: 'MySejahtera', icon: 'DevicePhoneMobileIcon', count: 2 },
  { key: 'Passport', label: 'Passport / Immigration', icon: 'IdentificationIcon', count: 2 },
  { key: 'Government Assistance', label: 'Government Assistance', icon: 'HandRaisedIcon', count: 1 },
  { key: 'Road Tax', label: 'Road Tax / JPJ', icon: 'TruckIcon', count: 1 },
  { key: 'Digital Identity', label: 'Digital Identity', icon: 'ShieldCheckIcon', count: 1 },
  { key: 'KWSP', label: 'KWSP / EPF', icon: 'BanknotesIcon', count: 1 },
  { key: 'Income Tax', label: 'Income Tax / LHDN', icon: 'CalculatorIcon', count: 1 },
  { key: 'Employment Insurance', label: 'Employment / PERKESO', icon: 'BriefcaseIcon', count: 1 },
];

export default function KnowledgeBaseContent() {
  const [search, setSearch] = useState('');
  const [activeCategory, setActiveCategory] = useState('all');
  const [selectedArticle, setSelectedArticle] = useState<Article | null>(null);
  const [showAI, setShowAI] = useState(true);

  const filtered = useMemo(() => {
    return articles.filter((a) => {
      const matchCat = activeCategory === 'all' || a.category === activeCategory;
      const matchSearch = !search || a.title.toLowerCase().includes(search.toLowerCase()) || a.agency.toLowerCase().includes(search.toLowerCase()) || a.tags.some((t) => t.toLowerCase().includes(search.toLowerCase()));
      return matchCat && matchSearch;
    });
  }, [search, activeCategory]);

  return (
    <div className="flex h-full">
      {/* Left Sidebar: Categories */}
      <div className="w-52 flex-shrink-0 border-r border-border bg-card overflow-y-auto scrollbar-thin py-3">
        <div className="px-3 mb-2">
          <div className="text-[11px] font-700 uppercase tracking-widest text-muted-foreground">Categories</div>
        </div>
        {categories.map((cat) => (
          <button
            key={`cat-${cat.key}`}
            onClick={() => { setActiveCategory(cat.key); setSelectedArticle(null); }}
            className={`w-full flex items-center gap-2.5 px-3 py-2 text-[12px] font-500 transition-all text-left
              ${activeCategory === cat.key ? 'bg-secondary text-primary font-600' : 'text-muted-foreground hover:bg-muted hover:text-foreground'}`}
          >
            <Icon name={cat.icon as Parameters<typeof Icon>[0]['name']} size={13} className="flex-shrink-0" />
            <span className="flex-1 truncate">{cat.label}</span>
            <span className="text-[10px] bg-muted text-muted-foreground rounded-full px-1.5 py-0.5 font-600 flex-shrink-0">{cat.count}</span>
          </button>
        ))}

        <div className="px-3 mt-4 mb-2">
          <div className="text-[11px] font-700 uppercase tracking-widest text-muted-foreground">Tools</div>
        </div>
        <button
          onClick={() => setShowAI((v) => !v)}
          className={`w-full flex items-center gap-2.5 px-3 py-2 text-[12px] font-500 transition-all text-left
            ${showAI ? 'bg-secondary text-primary font-600' : 'text-muted-foreground hover:bg-muted hover:text-foreground'}`}
        >
          <Icon name="SparklesIcon" size={13} className="flex-shrink-0 text-primary" />
          <span>Ask CX AI</span>
        </button>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex min-w-0 overflow-hidden">
        {/* Article List */}
        <div className={`flex flex-col ${selectedArticle ? 'w-72 flex-shrink-0 border-r border-border' : 'flex-1'} overflow-hidden`}>
          {/* Search */}
          <div className="p-4 border-b border-border bg-card flex-shrink-0">
            <div className="flex items-center gap-2">
              <div className="relative flex-1">
                <Icon name="MagnifyingGlassIcon" size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <input
                  type="text"
                  placeholder="Search knowledge base..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="w-full pl-8 pr-3 py-1.5 text-[13px] border border-border rounded bg-background focus:outline-none focus:ring-1 focus:ring-primary"
                />
              </div>
              {search && (
                <button onClick={() => setSearch('')} className="p-1.5 hover:bg-muted rounded transition-colors">
                  <Icon name="XMarkIcon" size={14} className="text-muted-foreground" />
                </button>
              )}
            </div>
            {search && (
              <div className="mt-2 text-[11px] text-muted-foreground">
                {filtered.length} result{filtered.length !== 1 ? 's' : ''} for "{search}"
              </div>
            )}
          </div>

          {/* Articles */}
          <div className="flex-1 overflow-y-auto scrollbar-thin p-4 space-y-2.5">
            {filtered.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 text-center">
                <Icon name="BookOpenIcon" size={32} className="text-muted-foreground/40 mb-2" />
                <div className="text-[13px] font-600 text-muted-foreground">No articles found</div>
                <div className="text-[12px] text-muted-foreground mt-1">Try a different search term or category</div>
              </div>
            ) : (
              filtered.map((article) => (
                <div
                  key={article.id}
                  onClick={() => setSelectedArticle(article)}
                  className={`card-elevated p-3 cursor-pointer transition-all hover:shadow-elevated hover:border-primary/30
                    ${selectedArticle?.id === article.id ? 'border-primary bg-secondary/30' : ''}`}
                >
                  <div className="flex items-start justify-between gap-2 mb-1.5">
                    <div className="flex-1 min-w-0">
                      <div className="text-[13px] font-600 text-foreground leading-snug mb-0.5">{article.title}</div>
                      <div className="text-[11px] text-muted-foreground">{article.agency}</div>
                    </div>
                    {article.verified && (
                      <span className="text-[10px] bg-green-50 text-green-600 border border-green-200 px-1.5 py-0.5 rounded font-600 flex-shrink-0 flex items-center gap-0.5">
                        <Icon name="CheckBadgeIcon" size={9} />
                        Verified
                      </span>
                    )}
                  </div>
                  {!selectedArticle && (
                    <p className="text-[11px] text-muted-foreground leading-relaxed mb-2 line-clamp-2">{article.summary}</p>
                  )}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="text-[10px] bg-secondary text-primary px-1.5 py-0.5 rounded font-500">{article.category}</span>
                      {article.tags.slice(0, 2).map((t) => (
                        <span key={`tag-${article.id}-${t}`} className="text-[10px] bg-muted text-muted-foreground px-1.5 py-0.5 rounded">{t}</span>
                      ))}
                    </div>
                    <div className="flex items-center gap-1.5 text-[10px] text-muted-foreground">
                      <Icon name="EyeIcon" size={10} />
                      {article.views.toLocaleString()}
                      <span className="ml-1">{article.updated}</span>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Article Detail */}
        {selectedArticle && (
          <div className="flex-1 min-w-0 overflow-y-auto scrollbar-thin border-r border-border">
            <KnowledgeArticleDetail article={selectedArticle} onClose={() => setSelectedArticle(null)} />
          </div>
        )}

        {/* AI Ask Panel */}
        {showAI && (
          <div className="w-80 flex-shrink-0 overflow-y-auto scrollbar-thin bg-ai-bg border-l border-ai-border">
            <AIAskPanel />
          </div>
        )}
      </div>
    </div>
  );
}